import type { LucideIcon } from "lucide-react";
import { LayoutGrid, MapPin, Users, UsersRound } from "lucide-react";
import { getAllProgramSlugs } from "@/content/programs";
import type { Stats } from "@/lib/api-types";
import type { NavItem } from "@/content/navigation";

export interface FooterProgramLink {
  label: string;
  href: string;
  slug: string;
}

export interface FooterStat {
  id: string;
  label: string;
  displayValue: string;
  icon: LucideIcon;
}

export const footerQuickLinks: NavItem[] = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About Us" },
  { href: "/programs", label: "Programs" },
  { href: "/impact", label: "Impact" },
  { href: "/events", label: "Events" },
  { href: "/donate", label: "Donate" },
  { href: "/contact", label: "Contact Us" },
];

export const footerPrograms: FooterProgramLink[] = [
  {
    label: "Healthcare Initiatives",
    href: "/programs/healthcare",
    slug: "healthcare",
  },
  {
    label: "Education Support",
    href: "/programs/education",
    slug: "education",
  },
  {
    label: "Goshala",
    href: "/programs/goshala",
    slug: "goshala",
  },
  {
    label: "Blood Donation",
    href: "/programs/blood-donation",
    slug: "blood-donation",
  },
  {
    label: "Blanket Distribution",
    href: "/programs/blanket-destitution",
    slug: "blanket-destitution",
  },
  {
    label: "Skill Development",
    href: "/programs/skill-development",
    slug: "skill-development",
  },
];

export const footerStatsFallbacks = {
  livesImpacted: 10_000,
  volunteers: 500,
  programs: 25,
  communities: 50,
} as const;

export const footerNewsletter = {
  heading: "Stay Connected",
  description: "Programs, events, and impact stories.",
  placeholder: "Enter your email address",
  submitLabel: "Subscribe",
  privacyNote: "Privacy respected.",
  successMessage: "Thank you for subscribing!",
} as const;

export function formatCompactCount(value: number): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${millions % 1 === 0 ? millions : millions.toFixed(1)}M+`;
  }
  if (value >= 1_000) {
    const thousands = Math.round(value / 1_000);
    return `${thousands}K+`;
  }
  return `${value}+`;
}

export function buildFooterStats(stats?: Stats): FooterStat[] {
  const programCount = getAllProgramSlugs().length;

  const livesImpacted =
    stats?.livesImpacted ?? footerStatsFallbacks.livesImpacted;
  const programs =
    stats?.projectsCompleted ??
    (programCount > 0 ? programCount : footerStatsFallbacks.programs);
  const volunteers = stats?.volunteers ?? footerStatsFallbacks.volunteers;
  const communities = stats?.communities ?? footerStatsFallbacks.communities;

  return [
    {
      id: "lives",
      label: "Lives Impacted",
      displayValue: formatCompactCount(livesImpacted),
      icon: UsersRound,
    },
    {
      id: "volunteers",
      label: "Volunteers",
      displayValue: formatCompactCount(volunteers),
      icon: Users,
    },
    {
      id: "programs",
      label: "Programs",
      displayValue: formatCompactCount(programs),
      icon: LayoutGrid,
    },
    {
      id: "communities",
      label: "Communities",
      displayValue: formatCompactCount(communities),
      icon: MapPin,
    },
  ];
}
